/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { css, useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import Button from '@Button';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import { getSolarSupplierName } from '../../Data/SolarSuppliers';
import {
  cancelSubscriptionMatchCheckAction,
  checkSubscriptionMatchAction,
  reportClientApiBookingNotConfirmableAction,
  submitClientApiBookingAction,
} from '../../Hooks/actionCreators';
import {
  goToNextScreen,
  goToPreviousScreen,
  resetSubscriptionMatch,
  setBookingOutcome,
} from '../../Lib/redux/slices/clientApiJourney';
import { exposeUserActionEvent } from '../../Helpers/events';
import { selectIsAddressListJourney } from '../../Helpers/clientApiJourneyPosition.helpers';
import { selectBookingLocation } from '../../Helpers/clientApiLocations.helpers';
import {
  selectHasSubscriptionMismatch,
  selectIsBookingConfirmable,
  selectSubscriptionMatchStatus,
} from '../../Helpers/subscriptionMatch.helpers';
import { useWidgetHeightRefresh } from '../../Hooks/useWidgetHeightRefresh/useWidgetHeightRefresh';
import {
  getFirstNameComponent,
  getLastNameComponent,
  getEmailComponent,
  getPhoneNumberComponent,
} from '../__helpers__/component.helpers';
import { useFallThroughToNormalLead } from '../../Hooks/useFallThroughToNormalLead';
import {
  BOOKING_PROGRESS_INTERVAL_MS,
  FALL_THROUGH_REASONS,
} from './clientApiJourney.constants';
import { getSlotDateLabel, getSlotTimeLabel } from './slotDateTime.helpers';
import { getAppointmentLabel } from './appointment.helpers';
import makeSlotPickerStyles from './makeSlotPickerStyles';
import { selectMatchedStrategyCollection } from '../../Helpers/strategyCollection.helpers';
import useOlbScreenCopy from './copy/useOlbScreenCopy';
import SolarSummaryCard from './SolarSummaryCard';
import { buildCardRows } from './olbScreenCopy.helpers';
import FailureNotice from './FailureNotice';

const fieldValue = (component) => component?.input?.value ?? '';

// The shared slot-picker heading centres its text; the solar steps are
// left-aligned, so the solar review layout overrides the alignment to match.
const alignLeft = css({
  textAlign: 'left',
  '& h6, & p': { textAlign: 'left' },
});

const bookingFallThroughReason = ({ hasSubscriptionMismatch, failing }) => {
  if (hasSubscriptionMismatch)
    return FALL_THROUGH_REASONS.SUBSCRIPTION_MISMATCH;
  if (failing) return FALL_THROUGH_REASONS.BOOKING_NOT_CONFIRMABLE;
  return FALL_THROUGH_REASONS.BOOKING_FAILED;
};

const ReviewBooking = ({ iFrameId, stepKey }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeSlotPickerStyles({ theme });
  const copy = useOlbScreenCopy(stepKey, 'review');
  const localeCode = useSelector((store) => store.formConfig?.localeCode);

  const isAddressJourney = useSelector(selectIsAddressListJourney);

  const components = useSelector((store) => store.components);
  const selectedLocation = useSelector(selectBookingLocation);
  const selectedSlot = useSelector(
    (store) => store.clientApiJourney.appointment?.selectedSlot
  );
  const bookingStatus = useSelector(
    (store) => store.clientApiJourney.appointment?.bookingStatus
  );
  const privacyPolicyUrl = useSelector(
    (store) => store.formConfig?.privacyPolicy?.link
  );
  const bookingReference = useSelector(
    (store) => store.clientApiJourney.appointment?.bookingReference
  );
  const isBookingConfirmable = useSelector(selectIsBookingConfirmable);
  const subscriptionMatchStatus = useSelector(selectSubscriptionMatchStatus);
  const hasSubscriptionMismatch = useSelector(selectHasSubscriptionMismatch);
  // The supplier of the subscription this journey is running for. No
  // human-readable name is baked into the strategy collection yet, so we report
  // the supplierId. TODO (GOLD-2343 follow-up): thread a readable supplier name
  // once Capture snapshots one.
  const supplierId = useSelector(
    (store) => selectMatchedStrategyCollection(store)?.supplierId
  );

  const firstName = fieldValue(getFirstNameComponent({ components }));
  const lastName = fieldValue(getLastNameComponent({ components }));
  const email = fieldValue(getEmailComponent({ components }));
  const phone = fieldValue(getPhoneNumberComponent({ components }));
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  const appointment = getAppointmentLabel(selectedSlot, localeCode);

  const personalRows = buildCardRows(
    isAddressJourney
      ? [{ label: copy.name, value: fullName }]
      : [
          { label: copy.name, value: fullName },
          { label: copy.email, value: email },
          { label: copy.phone, value: phone },
        ]
  );
  const clinicRow = isAddressJourney
    ? []
    : [{ label: copy.clinic, value: selectedLocation?.title }];
  const appointmentRows = buildCardRows([
    ...clinicRow,
    { label: copy.address, value: selectedLocation?.address },
    { label: copy.appointment, value: appointment },
  ]);

  // Solar carries its own consent wording and a fixed privacy-policy link, with
  // the supplier named from supplierId (the subcat is shared across suppliers);
  // Amplifon keeps the generic line and the form's configured link.
  const legal = isAddressJourney
    ? {
        prefix: copy.solarPrivacyPrefix,
        linkText: copy.solarPrivacyLinkText,
        link: copy.solarPrivacyLink,
        suffix: (copy.solarPrivacySuffix ?? '').replaceAll(
          '{supplier}',
          getSolarSupplierName(supplierId)
        ),
      }
    : {
        prefix: copy.privacyPrefix,
        linkText: copy.privacyLinkText,
        link: privacyPolicyUrl,
        suffix: copy.privacySuffix,
      };

  // Back would drop the result, which the effects below handle, and a second
  // Confirm would book twice. COMPLETED still renders here for a tick plus the
  // PageFade before the confirmation, so the disabled buttons, spinner and
  // message stay up then too.
  const isBookingInFlightOrCompleted = [
    FetchStatus.IN_PROGRESS,
    FetchStatus.COMPLETED,
  ].includes(bookingStatus);

  // The supplier's booking call can run for several seconds, so while it is in
  // flight the messages cycle under the spinner to show the wait is progress.
  // Capture copy is string labels elsewhere, so a single string is accepted
  // here as a one-message list.
  const progressMessages = [].concat(copy.bookingInProgress ?? []);
  const [progressIndex, setProgressIndex] = useState(0);
  useEffect(() => {
    if (!isBookingInFlightOrCompleted || progressMessages.length < 2) {
      setProgressIndex(0);
      return undefined;
    }
    const timer = setInterval(
      () => setProgressIndex((index) => (index + 1) % progressMessages.length),
      BOOKING_PROGRESS_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [isBookingInFlightOrCompleted, progressMessages.length]);

  useWidgetHeightRefresh([bookingStatus, progressIndex]);

  // Every answer exists by now, so the subscription is checked against them
  // here; coming back after Back re-runs it. Leaving cancels the check and
  // clears its result, so a stale one never gates Confirm.
  useEffect(() => {
    dispatch(checkSubscriptionMatchAction());

    return () => {
      dispatch(cancelSubscriptionMatchCheckAction());
      dispatch(resetSubscriptionMatch());
    };
  }, [dispatch]);

  const bookingActionDetails = () => ({
    supplierName: supplierId,
    store: selectedLocation?.title,
    date: selectedSlot?.start
      ? getSlotDateLabel(selectedSlot.start)
      : undefined,
    time: selectedSlot?.start
      ? getSlotTimeLabel(selectedSlot.start)
      : undefined,
    reference: bookingReference,
  });

  // Success path: advance to the confirmation once the booking completes.
  useEffect(() => {
    if (bookingStatus === FetchStatus.COMPLETED) {
      exposeUserActionEvent({
        type: 'bookingCompleted',
        iFrameId,
        actionDetails: bookingActionDetails(),
      });
      dispatch(setBookingOutcome({ bookingOutcome: 'BOOKED' }));
      dispatch(goToNextScreen());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStatus, dispatch]);

  // A Confirm click while the lead is not confirmable, a failed booking call or
  // a subscription that no longer matches all fall through to a normal lead.
  const [failing, setFailing] = useState(false);
  const hasFailed =
    failing || bookingStatus === FetchStatus.FAILED || hasSubscriptionMismatch;
  useEffect(() => {
    if (!hasFailed) return;

    exposeUserActionEvent({
      type: 'bookingNotCompleted',
      iFrameId,
      actionDetails: bookingActionDetails(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFailed]);
  useFallThroughToNormalLead({
    shouldFallThrough: hasFailed,
    reason: bookingFallThroughReason({ hasSubscriptionMismatch, failing }),
  });

  // The fade keeps this screen mounted briefly after Back, so cancel here
  // rather than waiting for the unmount cleanup.
  const handleBack = () => {
    dispatch(cancelSubscriptionMatchCheckAction());
    dispatch(resetSubscriptionMatch());
    dispatch(goToPreviousScreen());
  };

  const handleConfirm = () => {
    if (isBookingConfirmable !== true) {
      dispatch(reportClientApiBookingNotConfirmableAction());
      dispatch(setBookingOutcome({ bookingOutcome: 'BOOKING_FAILED' }));
      setFailing(true);
      return;
    }

    dispatch(submitClientApiBookingAction({ slot: selectedSlot }));
  };

  if (hasFailed) {
    return (
      <FailureNotice
        heading={copy.bookingFailed.heading}
        subheading={copy.bookingFailed.subheading}
        dataTestId="bookingFailed"
      />
    );
  }

  const isConfirming =
    isBookingInFlightOrCompleted ||
    subscriptionMatchStatus !== FetchStatus.COMPLETED;

  return (
    <Container css={classes.container} data-testid="reviewBooking">
      <Box css={classes.backButtonContainer}>
        <MuiButton
          css={[classes.toggleLink, isAddressJourney && classes.solarBackLink]}
          onClick={handleBack}
          disabled={isBookingInFlightOrCompleted}
          data-testid="reviewBack"
        >
          {copy.back}
        </MuiButton>
      </Box>
      <Box css={[classes.heading, isAddressJourney && alignLeft]}>
        <Text variant="question">{copy.heading}</Text>
      </Box>
      <Box css={[classes.subheading, isAddressJourney && alignLeft]}>
        <Text variant="text">{copy.subheading}</Text>
      </Box>

      {isAddressJourney ? (
        <SolarSummaryCard
          dataTestId="reviewAddressCard"
          rows={[...personalRows, ...appointmentRows]}
        />
      ) : (
        <>
          <SolarSummaryCard
            dataTestId="reviewPersonalCard"
            heading={copy.yourDetails}
            rows={personalRows}
          />
          <SolarSummaryCard
            dataTestId="reviewStoreCard"
            heading={copy.yourAppointment}
            rows={appointmentRows}
          />
        </>
      )}

      <Box
        css={[classes.legalLine, isAddressJourney && alignLeft]}
        data-testid="reviewLegal"
      >
        <Text variant="text">
          {legal.prefix}
          {legal.link ? (
            <a href={legal.link} target="_blank" rel="noreferrer">
              {legal.linkText}
            </a>
          ) : (
            legal.linkText
          )}
          {legal.suffix}
        </Text>
      </Box>

      <Button
        label={copy.confirm}
        onClick={handleConfirm}
        disabled={isConfirming}
        loading={isBookingInFlightOrCompleted}
        dataTestId="reviewConfirm"
      />
      {isBookingInFlightOrCompleted && progressMessages.length > 0 && (
        <Box
          css={classes.message}
          aria-live="polite"
          data-testid="reviewBookingProgress"
        >
          <Text variant="text">{progressMessages[progressIndex]}</Text>
        </Box>
      )}
    </Container>
  );
};

export default ReviewBooking;
